import { Injectable } from '@angular/core';
import { ItemAvaliavel } from '../../../core/config/services/avaliacao.service';
import { Periodo, ItemEducacional } from '../models/nota-types';

/**
 * Serviço responsável pelo processamento e ordenação de períodos
 * Separação de responsabilidades do NotaService
 */
@Injectable({
  providedIn: 'root'
})
export class PeriodoProcessorService {
  private readonly ORDEM_TIPO_PERIODO: Record<string, number> = {
    'PERIODO_AVALIATIVO': 0,
    'MEDIA_PERIODO': 0.5,
    'NOTA_PARA_EXAME': 1,
    'EXAME_FINAL': 2,
    'CONSELHO_CLASSE': 3,
    'CALCULO_MEDIA': 99
  };

  processPeriodosForYearView(
    itensAvaliaveisFiltrados: ItemAvaliavel[],
    periodos: Periodo[]
  ): Map<string, { id?: string; tipoPeriodo?: string; dataInicial?: string; data?: string; descricao?: string; [key: string]: unknown }> {
    const periodosProcessados = new Map<string, { id?: string; tipoPeriodo?: string; dataInicial?: string; data?: string; descricao?: string; [key: string]: unknown }>();

    this.addPeriodosAvaliativos(periodosProcessados, itensAvaliaveisFiltrados);
    this.addTipoPeriodoEspecial(periodosProcessados, itensAvaliaveisFiltrados, 'NOTA_PARA_EXAME', 'Nota para Exame');
    this.addExamesFinais(periodosProcessados, itensAvaliaveisFiltrados);
    this.addMediaPeriodo(periodosProcessados, itensAvaliaveisFiltrados);
    this.addTipoPeriodoEspecial(periodosProcessados, itensAvaliaveisFiltrados, 'CONSELHO_CLASSE', 'Conselho de Classe');
    this.addPeriodosDiretos(periodosProcessados, periodos);

    return periodosProcessados;
  }

  sortAndFilterPeriodos(
    periodosProcessados: Map<string, { id?: string; tipoPeriodo?: string; dataInicial?: string; data?: string; descricao?: string; [key: string]: unknown }>,
    itensAvaliaveisFiltrados: ItemAvaliavel[],
    itemEducacional: ItemEducacional
  ): Array<{ id?: string; tipoPeriodo?: string; dataInicial?: string; data?: string; descricao?: string; [key: string]: unknown }> {
    const periodosArray = Array.from(periodosProcessados.values())
      .sort((a, b) => this.comparePeriodos(a, b));

    return periodosArray.filter(periodo => 
      this.isPeriodoValido(periodo, itensAvaliaveisFiltrados, itemEducacional)
    );
  }

  comparePeriodos(
    a: { tipoPeriodo?: string; dataInicial?: string; data?: string },
    b: { tipoPeriodo?: string; dataInicial?: string; data?: string }
  ): number {
    // Compara por data inicial se ambas tiverem
    if (a.dataInicial && b.dataInicial) {
      return new Date(a.dataInicial).getTime() - new Date(b.dataInicial).getTime();
    }

    // Compara por tipo de período
    const ordemA = this.getOrdemTipoPeriodo(a.tipoPeriodo);
    const ordemB = this.getOrdemTipoPeriodo(b.tipoPeriodo);
    if (ordemA !== ordemB) {
      return ordemA - ordemB;
    }

    // Compara por data (data específica ou data inicial)
    const dataA = this.getPeriodoDate(a);
    const dataB = this.getPeriodoDate(b);
    return dataA - dataB;
  }

  private getOrdemTipoPeriodo(tipoPeriodo?: string): number {
    return tipoPeriodo ? (this.ORDEM_TIPO_PERIODO[tipoPeriodo] ?? 999) : 999;
  }

  private getPeriodoDate(periodo: { data?: string; dataInicial?: string }): number {
    if (periodo.data) {
      return new Date(periodo.data).getTime();
    }
    if (periodo.dataInicial) {
      return new Date(periodo.dataInicial).getTime();
    }
    return 0;
  }

  private isPeriodoValido(
    periodo: { id?: string; tipoPeriodo?: string },
    itensAvaliaveisFiltrados: ItemAvaliavel[],
    itemEducacional: ItemEducacional
  ): boolean {
    if (this.isMediaFinal(periodo)) {
      return false;
    }

    const itemAvaliavel = this.findItemAvaliavelForPeriodo(
      periodo,
      itensAvaliaveisFiltrados,
      itemEducacional
    );

    if (itemAvaliavel?.tipoPeriodo === 'NOTA_PARA_EXAME') {
      return this.isModoAvaliacaoPermiteNotaParaExame(itemAvaliavel.modoAvaliacao);
    }

    if (itemAvaliavel && this.isTipoCalculoOuNotaExame(itemAvaliavel.tipoPeriodo)) {
      return this.isModoAvaliacaoValido(itemAvaliavel.modoAvaliacao);
    }

    return true;
  }

  private isMediaFinal(periodo: { id?: string; tipoPeriodo?: string }): boolean {
    return periodo.id === 'MEDIA_FINAL' || periodo.tipoPeriodo === 'MEDIA_FINAL';
  }

  private findItemAvaliavelForPeriodo(
    periodo: { id?: string; tipoPeriodo?: string },
    itensAvaliaveisFiltrados: ItemAvaliavel[],
    itemEducacional: ItemEducacional
  ): ItemAvaliavel | undefined {
    return itensAvaliaveisFiltrados.find(item => {
      const itemWithPeriodo = item as ItemAvaliavel & { idPeriodo?: number };
      const periodoId = periodo.id;
      return (itemWithPeriodo.idPeriodo?.toString() === periodoId || 
              item.tipoPeriodo === periodo.tipoPeriodo) &&
             item.itemEducacional?.id === itemEducacional.id;
    });
  }

  private isTipoCalculoOuNotaExame(tipoPeriodo?: string): boolean {
    return tipoPeriodo === 'CALCULO_MEDIA' || tipoPeriodo === 'NOTA_PARA_EXAME';
  }

  private isModoAvaliacaoValido(modoAvaliacao?: string): boolean {
    return modoAvaliacao !== 'DESCRITIVO' && modoAvaliacao !== 'CONCEITO';
  }

  private isModoAvaliacaoPermiteNotaParaExame(modoAvaliacao?: string): boolean {
    return modoAvaliacao !== 'DESCRITIVO' && modoAvaliacao !== 'CONCEITO' && modoAvaliacao !== 'CONCEITO_NUMERICO';
  }

  private addPeriodosAvaliativos(
    periodosProcessados: Map<string, { id?: string; tipoPeriodo?: string; dataInicial?: string; data?: string; descricao?: string; [key: string]: unknown }>,
    itensAvaliaveisFiltrados: ItemAvaliavel[]
  ): void {
    const tiposExcluidos = ['INSTRUMENTO', 'CALCULO_INSTRUMENTOS', 'MEDIA_PERIODO', 'EXAME_FINAL',
                            'MEDIA_FINAL', 'CALCULO_MEDIA', 'CONSELHO_CLASSE', 'NOTA_PARA_EXAME'];
    
    itensAvaliaveisFiltrados.forEach(item => {
      if (item.periodoAvaliativo && !tiposExcluidos.includes(item.tipoPeriodo)) {
        const periodoId = item.periodoAvaliativo.id.toString();
        if (!periodosProcessados.has(periodoId)) {
          periodosProcessados.set(periodoId, {
            id: periodoId,
            descricao: item.periodoAvaliativo.descricao || item.descricao,
            dataInicial: item.periodoAvaliativo.dataInicial,
            dataFinal: item.periodoAvaliativo.dataFinal,
            periodoAvaliativo: item.periodoAvaliativo,
            tipoPeriodo: 'PERIODO_AVALIATIVO'
          });
        }
      }
    });
  }


  private addTipoPeriodoEspecial(
    periodosProcessados: Map<string, { id?: string; tipoPeriodo?: string; dataInicial?: string; data?: string; descricao?: string; [key: string]: unknown }>,
    itensAvaliaveisFiltrados: ItemAvaliavel[],
    tipoPeriodo: string,
    descricaoPadrao: string
  ): void {
    const item = itensAvaliaveisFiltrados.find(i => i.tipoPeriodo === tipoPeriodo);
    if (item) {
      const periodoId = item.idPeriodo?.toString() || tipoPeriodo;
      if (!periodosProcessados.has(periodoId)) {
        periodosProcessados.set(periodoId, {
          id: periodoId,
          descricao: item.descricao || descricaoPadrao,
          tipoPeriodo,
          data: item.data,
          rawValue: item
        });
      }
    }
  }

  private addMediaPeriodo(
    periodosProcessados: Map<string, { id?: string; tipoPeriodo?: string; dataInicial?: string; data?: string; descricao?: string; [key: string]: unknown }>,
    itensAvaliaveisFiltrados: ItemAvaliavel[]
  ): void {
    const itemMediaPeriodo = itensAvaliaveisFiltrados.find(item =>
      item.tipoPeriodo === 'MEDIA_PERIODO' && !item.periodoAvaliativo
    );
    if (itemMediaPeriodo) {
      const periodoId = 'MEDIA_PERIODO';
      if (!periodosProcessados.has(periodoId)) {
        periodosProcessados.set(periodoId, {
          id: periodoId,
          descricao: 'Média do período',
          tipoPeriodo: 'MEDIA_PERIODO',
          data: itemMediaPeriodo.data,
          rawValue: itemMediaPeriodo
        });
      }
    }
  }

  private addExamesFinais(
    periodosProcessados: Map<string, { id?: string; tipoPeriodo?: string; dataInicial?: string; data?: string; descricao?: string; [key: string]: unknown }>,
    itensAvaliaveisFiltrados: ItemAvaliavel[]
  ): void {
    const examesFinaisUnicos = new Map<string, ItemAvaliavel>();
    itensAvaliaveisFiltrados
      .filter(item => item.tipoPeriodo === 'EXAME_FINAL')
      .forEach(item => {
        const identificador = item.identificadorExameFinal || item.id?.toString();
        if (identificador && !examesFinaisUnicos.has(identificador)) {
          examesFinaisUnicos.set(identificador, item);
        }
      });

    examesFinaisUnicos.forEach((item, identificador) => {
      const periodoId = item.idPeriodo?.toString() || `EXAME_FINAL_${identificador}`;
      if (!periodosProcessados.has(periodoId)) {
        periodosProcessados.set(periodoId, {
          id: periodoId,
          descricao: item.descricao || `Exame Final ${identificador}`,
          tipoPeriodo: 'EXAME_FINAL',
          identificadorExameFinal: identificador,
          data: item.data,
          rawValue: item
        });
      }
    });
  }

  private addPeriodosDiretos(
    periodosProcessados: Map<string, { id?: string; tipoPeriodo?: string; dataInicial?: string; data?: string; descricao?: string; [key: string]: unknown }>,
    periodos: Periodo[]
  ): void {
    if (!periodos || periodos.length === 0) {
      return;
    }

    periodos.forEach((periodo: Periodo) => {
      if (periodo?.id) {
        const periodoId = periodo.id.toString();
        if (!periodosProcessados.has(periodoId)) {
          periodosProcessados.set(periodoId, {
            id: periodoId,
            descricao: periodo.descricao,
            dataInicial: periodo.dataInicial,
            dataFinal: periodo.dataFinal,
            periodoAvaliativo: periodo,
            tipoPeriodo: 'PERIODO_AVALIATIVO'
          });
        }
      }
    });
  }
}

